import React, { createContext, useMemo, useState } from 'react';
import { GoogleOAuthProvider } from "@react-oauth/google";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { ThemeProvider } from "@mui/material";

import Router from "./routes/router";
import { createAppTheme } from "./theme/MuiTheme";
import CookieConsent from './components/cookieConsent/CookieConsent';

import './App.scss';

export const ColorModeContext = createContext({
  toggleColorMode: () => {},
  mode: 'light',
});

// react-snap (Puppeteer) sets the user agent to 'ReactSnap' during pre-rendering.
// GoogleOAuthProvider injects <script src="gsi/client"> into <head> when it mounts.
// If we let it mount during prerendering, that script tag gets baked into the
// saved HTML. When a real user loads the page, the browser runs the baked-in
// script, then React mounts and GoogleOAuthProvider injects it AGAIN —
// resulting in google.accounts.id.initialize() being called twice, which
// breaks the sign-in button. Skipping the provider during prerendering
// keeps the saved HTML clean.
const isPrerender = typeof navigator !== 'undefined' && navigator.userAgent === 'ReactSnap';

function App() {
  const [mode, setMode] = useState(() => {
    return localStorage.getItem('nc_color_mode') || 'light';
  });

  const colorMode = useMemo(() => ({
    toggleColorMode: () => {
      setMode((prev) => {
        const next = prev === 'light' ? 'dark' : 'light';
        localStorage.setItem('nc_color_mode', next);
        return next;
      });
    },
    mode,
  }), [mode]);

  const theme = useMemo(() => createAppTheme(mode), [mode]);

  const inner = (
    <ColorModeContext.Provider value={colorMode}>
      <ThemeProvider theme={theme}>
        <div className={`app-bg app-bg--${mode}`}>
          <Router />
        </div>
        <CookieConsent />
        <ToastContainer
          position="bottom-right"
          autoClose={3000}
          hideProgressBar={false}
          closeOnClick
          pauseOnHover
          theme={mode}
        />
      </ThemeProvider>
    </ColorModeContext.Provider>
  );

  if (isPrerender) return inner;

  return (
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
      {inner}
    </GoogleOAuthProvider>
  );
}

export default App;


